/* @sick-leave-v1 — Αναρρωτική Άδεια, admin report.
 *
 * @sick-leave-admin-v2 (16 Oct 2026): rebuilt to look and read exactly like
 * the renewals report («Επαναλήψεις Συνταγών») — month picker, totals,
 * per-doctor table, filter chips, and a request table with patient, doctor,
 * Greek status, decision, refund and answer time. Paid requests only; an
 * unpaid checkout (status 'expired' with no payment) charged nobody and is
 * shown only as a count, so it no longer looks like a lost €10.
 *
 * Still a separate page from the renewals report: this ships without touching
 * a report that is already live on real money.
 *
 * NO CLINICAL CONTENT. The endpoint returns money, outcomes and timings only —
 * never answers, symptoms or a diagnosis. Keep it that way.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sickLeaveAdminAPI } from '../services/api';

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, '0')}`];
};
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const mins = (m) => (m == null ? '—' : m < 60 ? `${m}′` : `${Math.floor(m / 60)}ω ${m % 60}′`);

const STATUS = {
  open: ['Στην ουρά', '#E07B2A'],
  claimed: ['Σε εξέλιξη', '#1E3A5F'],
  info_requested: ['Αναμονή ασθενή', '#6b7280'],
  completed: ['Ολοκληρώθηκε', '#0D9A6A'],
  expired: ['Έληξε χωρίς γιατρό', '#B91C1C'],
  closed_no_reply: ['Χωρίς απάντηση ασθενή', '#6b7280'],
};
const DECISION = {
  issued: ['Εκδόθηκε', '#0D9A6A'],
  declined: ['Άρνηση', '#B91C1C'],
  referred: ['Παραπομπή', '#E07B2A'],
};
const REFUND = {
  refunded: ['Επιστράφηκαν', '#C0185A'],
  pending: ['Επιστροφή σε εξέλιξη', '#E07B2A'],
  failed: ['ΑΠΕΤΥΧΕ — χειροκίνητα στο Viva', '#B91C1C'],
};

const Pill = ({ text, color }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
    color, background: color + '18', whiteSpace: 'nowrap' }}>{text}</span>
);
const Stat = ({ value, label, warn }) => (
  <div style={{ background: '#fff', border: `1px solid ${warn ? '#FCA5A5' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 16px', minWidth: 130, flex: '1 1 130px' }}>
    <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#B91C1C' : '#0F1F3D' }}>{value}</div>
    <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
  </div>
);

export default function SickLeaveReportPage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const [from, to] = monthRange(month);
    sickLeaveAdminAPI.list({ from, to })
      .then((r) => { if (!cancelled) setData(r.data?.data || null); })
      .catch(() => { if (!cancelled) { setData(null); toast.error('Αδυναμία φόρτωσης αναρρωτικών αδειών'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const s = data?.summary;
  // Until the backend change is live the response has only rows/totals;
  // drop unpaid checkouts here too so the table is right either way.
  const all = (data?.requests || data?.rows || []).filter((r) => r.paidAt);
  const rows = all.filter((r) => {
    if (filter === 'refunded') return r.refundStatus === 'refunded' || r.refundStatus === 'failed' || r.refundStatus === 'pending';
    if (filter === 'live') return ['open', 'claimed', 'info_requested'].includes(r.status);
    if (filter === 'issued') return r.decision === 'issued';
    return true;
  });
  const euros = (n) => `€${Number(n || 0).toFixed(2)}`;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Αναρρωτικές Άδειες</h1>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>
            Αιτήματα που πληρώθηκαν τον μήνα (ώρα Αθήνας). Χωρίς συμπτώματα και απαντήσεις ασθενών — αυτά τα βλέπει μόνο ο γιατρός.{' '}
            <Link to="/renewals">Επαναλήψεις συνταγών →</Link>
          </p>
        </div>
        <label style={{ fontSize: 13, color: '#374151' }}>
          Μήνας{' '}
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </label>
      </div>

      {s && s.refundFailed > 0 && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {s.refundFailed} επιστροφή/ές απέτυχαν στο Viva και θέλουν χειροκίνητη επιστροφή (έχει σταλεί email στο support).
        </div>
      )}
      {s && s.certificatesFailed > 0 && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {s.certificatesFailed} βεβαίωση/εις δεν καταχωρήθηκαν και θέλουν χειροκίνητη ενέργεια.
        </div>
      )}

      {s && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          <Stat value={s.paid} label="Πληρωμένα αιτήματα" />
          <Stat value={s.completed} label="Ολοκληρώθηκαν" />
          <Stat value={s.open} label="Σε εξέλιξη τώρα" />
          <Stat value={`${s.issued} / ${s.declined} / ${s.referred}`} label="Εκδόθηκαν / Αρνήσεις / Παραπομπές" />
          <Stat value={s.refunded} label="Επιστροφές €10" />
          <Stat value={mins(s.avgMinutesToDecision)} label="Μέσος χρόνος απάντησης" />
          <Stat value={euros(s.keptRevenueEuros)} label="Εισπράξεις (χωρίς τις επιστροφές)" />
          <Stat value={s.abandonedCheckouts} label="Δεν ολοκλήρωσαν πληρωμή (καμία χρέωση)" />
        </div>
      )}

      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 8px' }}>Ανά γιατρό</h2>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto', marginBottom: 22 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Γιατρός</th>
              <th style={th}>Αιτήματα</th>
              <th style={th}>Εκδόθηκαν / Αρνήσεις / Παραπομπές</th>
              <th style={th}>Επιστροφές</th>
              <th style={th}>Μέσος χρόνος</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={5}>Φόρτωση…</td></tr>}
            {!loading && (data?.byDoctor || []).length === 0 && (
              <tr><td style={td} colSpan={5}>Δεν υπάρχουν ολοκληρωμένα αιτήματα αυτόν τον μήνα.</td></tr>
            )}
            {!loading && (data?.byDoctor || []).map((d) => (
              <tr key={d.doctor}>
                <td style={td}>{d.doctor}</td>
                <td style={td}>{d.completed}</td>
                <td style={td}>{d.issued} / {d.declined} / {d.referred}</td>
                <td style={td}>{d.refunded}</td>
                <td style={td}>{mins(d.avgMinutes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, margin: '4px 0 8px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Αιτήματα</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['all', 'Όλα'], ['live', 'Σε εξέλιξη'], ['issued', 'Εκδόθηκαν'], ['refunded', 'Επιστροφές']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              style={{ padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: '1px solid ' + (filter === k ? '#C0185A' : '#e5e7eb'),
                background: filter === k ? '#C0185A' : '#fff', color: filter === k ? '#fff' : '#374151' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Πληρωμή</th>
              <th style={th}>Ασθενής</th>
              <th style={th}>Γιατρός</th>
              <th style={th}>Κατάσταση</th>
              <th style={th}>Απόφαση</th>
              <th style={th}>Βεβαίωση</th>
              <th style={th}>Επιστροφή</th>
              <th style={th}>Απάντηση σε</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={8}>Φόρτωση…</td></tr>}
            {!loading && rows.length === 0 && <tr><td style={td} colSpan={8}>Κανένα αίτημα.</td></tr>}
            {!loading && rows.map((r) => {
              const st = STATUS[r.status] || [r.status, '#6b7280'];
              const dc = r.decision ? DECISION[r.decision] : null;
              const rf = REFUND[r.refundStatus];
              return (
                <tr key={r.id} style={{ background: (r.refundStatus === 'failed' || r.certificateFailed) ? '#FEF2F2' : 'transparent' }}>
                  <td style={td}>{fmtDateTime(r.paidAt)}</td>
                  <td style={td}>{r.patientName || '—'}</td>
                  <td style={td}>{r.doctorName || '—'}</td>
                  <td style={td}><Pill text={st[0]} color={st[1]} /></td>
                  <td style={td}>{dc ? <Pill text={dc[0]} color={dc[1]} /> : '—'}</td>
                  <td style={td}>
                    {/* @sick-leave-cert-choice-v1 (24 Oct 2026): how it was issued. The doctor
                     * picks ΗΔΙΚΑ or a private DocNow note; only the private one leaves a PDF. */}
                    {r.certificateFailed
                      ? <Pill text="Απέτυχε" color="#B91C1C" />
                      : r.decision !== 'issued' ? '—'
                      : r.certificateIssued ? 'Ιδιωτική' : 'ΗΔΙΚΑ'}
                  </td>
                  <td style={td}>{rf ? <Pill text={rf[0]} color={rf[1]} /> : '—'}</td>
                  <td style={td}>{mins(r.minutesToDecision)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 14 }}>
        Γιατροί στην υπηρεσία: <Link to="/doctors">Ιατροί</Link> → γιατρός → Κλινικές → «Αναρρωτική άδεια».
        Η αμοιβή (€10, γιατρός €6 / DocNow €4) ορίζεται από την υπηρεσία. Αν κανένας γιατρός δεν αναλάβει
        το αίτημα εγκαίρως, επιστρέφεται αυτόματα και έρχεται email στο support. Μια αποτυχημένη επιστροφή ή
        βεβαίωση δεν διορθώνεται μόνη της — χρειάζεται χειροκίνητη ενέργεια.
      </p>
    </div>
  );
}
